import { useState, useEffect, useRef } from 'react';
import ImageUploader from './components/ImageUploader';
import PDFRightPane from './components/PDFRightPane';
import PDFSidebar from './components/PDFSidebar';
import SettingsDialog from './components/SettingsDialog';
import AutoOptimizeDialog from './components/AutoOptimizeDialog';
import FilterControls from './components/FilterControls';
import AdjustmentPanel from './components/AdjustmentPanel';
import AdSenseBlock from './components/AdSenseBlock';
import { useLazyPDFProcessor } from './hooks/useLazyPDFProcessor';
import { ThemeProvider } from './contexts/ThemeContext';
import { calculateInkUsage, getImageData, estimateFilterSavings } from './utils/inkCalculator';

function App() {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [currentPDFPage, setCurrentPDFPage] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  const [showExceptionManager, setShowExceptionManager] = useState(false);
  const [initialViewMode, setInitialViewMode] = useState('contactSheet');
  const [currentViewMode, setCurrentViewMode] = useState('preview'); // Track current view mode
  const [selectedContactSheetPages, setSelectedContactSheetPages] = useState(new Set()); // Track selected pages in Contact Sheet
  const [showAutoOptimizeDialog, setShowAutoOptimizeDialog] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);
  const [isAnalyzingPages, setIsAnalyzingPages] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState({ current: 0, total: 0 });
  const [sidebarWidth, setSidebarWidth] = useState(320); // Default sidebar width in pixels
  const [isResizing, setIsResizing] = useState(false);
  const [pageSettingMode, setPageSettingMode] = useState(''); // Track page setting mode for adjustment panel
  const [forcePresetCreation, setForcePresetCreation] = useState(false); // Track force preset creation flag
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768); // Track if viewport is mobile
  const [originalFiltersBeforeGlobal, setOriginalFiltersBeforeGlobal] = useState(null); // Store filters before global adjustment for cancel
  const [previewFilters, setPreviewFilters] = useState(null); // Preview filters being edited (not yet committed)
  const [previewPages, setPreviewPages] = useState(new Set()); // Pages that should show preview
  const setManualModeChangeCallbackRef = useRef(null); // Store callback from PDFSidebar to set manual mode change
  const clearManualModeChangeCallbackRef = useRef(null); // Store callback from PDFSidebar to clear manual mode change (for cancel)
  const cancelCustomAdjustmentCallbackRef = useRef(null); // Store callback from PDFSidebar to cancel custom adjustment and restore original filters
  const setPageSettingModeCallbackRef = useRef(null); // Store callback from PDFSidebar to set pageSettingMode (for keeping button highlighted)
  const [manualModeChange, setManualModeChange] = useState(false); // Track manual button clicks (lifted from PDFSidebar)
  const [modeWasAutoSelected, setModeWasAutoSelected] = useState(false); // Track if current mode was auto-selected (lifted from PDFSidebar)
  const [bottomSheetOpen, setBottomSheetOpen] = useState(false); // Mobile bottom sheet state (lifted from PDFSidebar)
  const [originalPageExceptions, setOriginalPageExceptions] = useState(null); // Store original page exceptions for Preset bottom sheet cancel
  const [previewedPresetId, setPreviewedPresetId] = useState(null); // Track which preset is currently being previewed
  const [originalCustomFilters, setOriginalCustomFilters] = useState(null); // Store original filters when entering Custom mode

  // Debug: Log when pageSettingMode changes
  useEffect(() => {
    console.log('[App] pageSettingMode changed to:', pageSettingMode);
  }, [pageSettingMode]);

  // Debug: Log when preview state changes
  useEffect(() => {
    console.log('[App] previewFilters changed:', previewFilters);
    console.log('[App] previewPages changed:', Array.from(previewPages));
  }, [previewFilters, previewPages]);

  // Lazy PDF processing hook
  const {
    extractedPages,
    currentProcessedPage,
    filters: pdfFilters,
    updateFilter: updatePDFFilter,
    resetFilters: resetPDFFilters,
    downloadProcessedPDF,
    isExtracting,
    isProcessingPage,
    extractProgress,
    error,
    settings,
    updateSettings,
    processAllPages,
    processMultiplePages,
    pageExceptions,
    addPageException,
    updatePageException,
    deletePageException,
    togglePageException,
    applyToSelectedPages,
    preservePagesAsOriginal,
    applyGlobalToPages,
    getEffectiveFilters,
    processedPagesCache,
    savedPresets,
    savePreset,
    deletePreset,
    applyPresetToPages,
  } = useLazyPDFProcessor(
    uploadedFile?.file,
    currentPDFPage
  );

  // Check for saved default view mode on mount
  useEffect(() => {
    const savedMode = localStorage.getItem('ecoprint_default_view_mode');
    if (savedMode) {
      setInitialViewMode(savedMode === 'contactSheet' ? 'contactSheet' : 'preview');
    }
  }, []);



  // Handle viewport resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle sidebar resize
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;

      const minWidth = 280; // Minimum sidebar width (slightly larger than current 256px/64*4)
      const maxWidth = 600; // Maximum sidebar width
      const newWidth = Math.max(minWidth, Math.min(maxWidth, e.clientX));

      setSidebarWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  const handleFileUpload = (fileData) => {
    setUploadedFile(fileData);
    setInitialViewMode('contactSheet');
    setShowViewModeSelection(false); // Ensure this is always false
  };



  const handleViewModeChange = (mode) => {
    setCurrentViewMode(mode);
  };

  const handleSelectedPagesChange = (pages) => {
    setSelectedContactSheetPages(pages);
  };

  const handleReset = () => {
    setUploadedFile(null);
    setCurrentPDFPage(0);
    resetPDFFilters();
  };

  const handleSavePreset = (currentFilters, currentPageOrPages, providedName = null) => {
    // Use provided name or prompt for one
    const name = providedName || prompt('Enter a name for this optimization preset:');
    if (name && name.trim()) {
      const presetName = name.trim();
      savePreset(presetName, currentFilters);

      // Apply to page(s) - handle both single page number and array of pages
      if (currentPageOrPages) {
        const pagesToApply = Array.isArray(currentPageOrPages) ? currentPageOrPages : [currentPageOrPages];
        pagesToApply.forEach(pageNum => {
          addPageException(pageNum, currentFilters, presetName);
        });
      }

      // Only show alert if using prompt (not when called from bottom sheet)
      if (!providedName) {
        const pageCount = Array.isArray(currentPageOrPages) ? currentPageOrPages.length : 1;
        const pageText = pageCount === 1 ? `page ${currentPageOrPages}` : `${pageCount} pages`;
        alert(`Preset "${presetName}" saved and applied to ${pageText}!`);
      }
    }
  };

  const handleSaveAsGlobal = (currentFilters) => {
    if (window.confirm('Apply global settings to all pages without custom adjustments?\n\nThis will apply to pages that don\'t have Preserve, Preset, or Custom settings.')) {
      // Update PDF filters
      Object.keys(currentFilters).forEach(key => {
        updatePDFFilter(key, currentFilters[key]);
      });

      // Find all pages without exceptions and apply global settings to them
      const totalPages = extractedPages.length;
      const pagesWithoutExceptions = [];

      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        const exception = pageExceptions[pageNum];
        // Only include pages that have NO exception or exception is disabled
        if (!exception || !exception.enabled) {
          pagesWithoutExceptions.push(pageNum);
        }
      }

      console.log('📋 Pages without exceptions:', pagesWithoutExceptions);

      // Apply global settings to these pages
      if (pagesWithoutExceptions.length > 0) {
        applyGlobalToPages(pagesWithoutExceptions);
        alert(`Global settings applied to ${pagesWithoutExceptions.length} page(s) without custom adjustments!`);
      } else {
        alert('All pages already have custom adjustments. No pages were updated.');
      }
    }
  };

  const handleAutoOptimize = async (pageNumbers) => {
    console.log('🔍 Auto-Optimize triggered for pages:', pageNumbers);

    if (!pageNumbers || pageNumbers.length === 0) {
      console.log('❌ No pages to analyze');
      return;
    }

    // Show progress overlay
    setIsAnalyzingPages(true);
    setAnalysisProgress({ current: 0, total: pageNumbers.length });

    try {
      const analysisResults = [];
      const pageImages = {}; // Store original and preview images
      let totalSavings = 0;
      let preserveCount = 0;
      let optimizeCount = 0;

      // Get auto-optimize settings
      const autoOptimizeSettings = settings.autoOptimize || {
        allowPreserve: true,
        requireReview: true,
        minSavingsPercent: 10,
      };

      console.log('⚙️ Settings:', autoOptimizeSettings);

      // Analyze each page
      for (let i = 0; i < pageNumbers.length; i++) {
        const pageNumber = pageNumbers[i];

        // Update progress
        setAnalysisProgress({ current: i + 1, total: pageNumbers.length });

        console.log(`📄 Analyzing page ${pageNumber}... (${i + 1}/${pageNumbers.length})`);

        // Get the page's current effective filters
        const effectiveFilters = getEffectiveFilters(pageNumber);
        console.log(`   Effective filters:`, effectiveFilters);

        // Get the image data URL for this page
        // processedPagesCache contains data URLs directly
        // extractedPages contains objects with imageDataUrl property
        let imageDataUrl = processedPagesCache[pageNumber];

        if (!imageDataUrl) {
          // Try extractedPages - it's an array with 0-based index
          const extractedPage = extractedPages[pageNumber - 1];
          if (extractedPage) {
            imageDataUrl = extractedPage.imageDataUrl;
          }
        }

        console.log(`   Image data URL available:`, !!imageDataUrl);
        console.log(`   Image data URL type:`, typeof imageDataUrl);

        if (!imageDataUrl) {
          console.log(`   ⚠️ Skipping page ${pageNumber} - no image data available`);
          continue;
        }

        // Check if imageDataUrl is a valid data URL
        if (typeof imageDataUrl !== 'string' || !imageDataUrl.startsWith('data:')) {
          console.log(`   ⚠️ Skipping page ${pageNumber} - invalid data format (expected data URL)`);
          console.log(`   Got:`, typeof imageDataUrl, imageDataUrl?.substring?.(0, 50));
          continue;
        }

        // Create an image element from the image data URL
        console.log(`   Loading image for page ${pageNumber}...`);
        const img = new Image();

        try {
          await new Promise((resolve, reject) => {
            img.onload = () => {
              console.log(`   ✅ Image loaded: ${img.width}x${img.height}`);
              resolve();
            };
            img.onerror = (e) => {
              console.error(`   ❌ Image load failed for page ${pageNumber}:`, e);
              reject(new Error(`Failed to load image for page ${pageNumber}`));
            };
            img.src = imageDataUrl;
          });
        } catch (imgError) {
          console.log(`   ⚠️ Skipping page ${pageNumber} due to image load error`);
          continue;
        }

        // Calculate current ink usage
        console.log(`   Calculating ink usage...`);
        const imageData = getImageData(img);
        const currentInkStats = calculateInkUsage(imageData);
        const currentInkUsage = currentInkStats.avgInkUsage;
        console.log(`   Ink usage: ${currentInkUsage}%, Dark coverage: ${currentInkStats.darkCoverage}%`);

        // Estimate savings from current filters
        const currentSavings = estimateFilterSavings(effectiveFilters);

        // Determine if we should suggest optimization
        let suggestion = 'optimize';
        let reason = '';
        let estimatedSavings = 0;
        let suggestedFilters = { ...effectiveFilters };

        // Check if page is already well-optimized
        if (currentInkUsage < 30) {
          // Low ink usage already - suggest preserve
          if (autoOptimizeSettings.allowPreserve) {
            suggestion = 'preserve';
            reason = 'Already well-optimized with low ink usage';
          } else {
            suggestion = 'optimize';
            reason = 'Could optimize further for maximum savings';
            estimatedSavings = Math.max(5, Math.round(currentInkUsage * 0.3));
          }
        } else if (currentSavings.estimatedSavings >= autoOptimizeSettings.minSavingsPercent) {
          // Good savings already applied
          if (autoOptimizeSettings.allowPreserve) {
            suggestion = 'preserve';
            reason = `Current settings already provide ${currentSavings.estimatedSavings}% savings`;
          } else {
            suggestion = 'optimize';
            reason = 'Could optimize with additional filters';
            estimatedSavings = Math.max(5, Math.round((100 - currentSavings.estimatedSavings) * 0.2));
          }
        } else {
          // Needs optimization
          suggestion = 'optimize';

          // Determine what optimizations would help
          let potentialSavings = 0;
          let bestPreset = null;
          let bestPresetSavings = 0;

          // Check if we should consider presets
          const enabledPresetIds = autoOptimizeSettings.enabledPresets || [];
          if (enabledPresetIds.length > 0 && savedPresets) {
            // Evaluate each enabled preset to find the best one
            for (const presetId of enabledPresetIds) {
              const preset = savedPresets.find(p => p.id === presetId);
              if (!preset) continue;

              // Estimate savings from this preset
              const presetSavings = estimateFilterSavings(preset.settings);
              if (presetSavings.estimatedSavings > bestPresetSavings) {
                bestPresetSavings = presetSavings.estimatedSavings;
                bestPreset = preset;
              }
            }
          }

          // Compare preset savings with rule-based optimizations
          let ruleSavings = 0;
          let ruleReason = '';

          if (currentInkStats.darkCoverage > 50 && !effectiveFilters.invert) {
            ruleSavings = 40;
            ruleReason = 'High dark coverage - invert colors for major savings';
          } else if (!effectiveFilters.brightness || effectiveFilters.brightness < 120) {
            ruleSavings = 25;
            ruleReason = 'Increase brightness to reduce ink usage';
          } else if (!effectiveFilters.grayscale) {
            ruleSavings = 20;
            ruleReason = 'Convert to grayscale to save color ink';
          } else {
            ruleSavings = 15;
            ruleReason = 'Apply combined optimizations for ink savings';
          }

          // Choose the better option: preset or rule-based
          if (bestPreset && bestPresetSavings > ruleSavings) {
            potentialSavings = bestPresetSavings;
            reason = `Apply "${bestPreset.name}" preset for optimal savings`;
            // Store the preset for later application
            suggestedFilters.usePreset = bestPreset;
          } else {
            potentialSavings = ruleSavings;
            reason = ruleReason;

            // Apply rule-based filters
            if (reason.includes('invert')) {
              suggestedFilters.invert = true;
            } else if (reason.includes('brightness')) {
              suggestedFilters.brightness = 140;
            } else if (reason.includes('grayscale')) {
              suggestedFilters.grayscale = true;
            }
          }

          estimatedSavings = Math.min(potentialSavings, 100 - currentSavings.estimatedSavings);
        }

        // Track statistics
        if (suggestion === 'preserve') {
          preserveCount++;
        } else {
          optimizeCount++;
          totalSavings += estimatedSavings;
        }

        analysisResults.push({
          pageNumber,
          suggestion,
          reason,
          currentInk: Math.round(currentInkUsage),
          estimatedSavings: Math.round(estimatedSavings),
          suggestedPreset: suggestedFilters.usePreset || null,
        });

        // Store original image and generate preview
        pageImages[pageNumber] = {
          original: imageDataUrl,
          preview: null,
        };

        // Generate preview for optimize suggestions
        if (suggestion === 'optimize') {
          try {
            let previewFilters;

            // Use preset settings if a preset was suggested
            if (suggestedFilters.usePreset) {
              previewFilters = { ...suggestedFilters.usePreset.settings };
              console.log(`   🎨 Using preset "${suggestedFilters.usePreset.name}" for preview`);
            } else {
              // Determine filters to apply based on reason
              previewFilters = { ...effectiveFilters };

              if (reason.includes('invert')) {
                previewFilters.invert = true;
              }
              if (reason.includes('brightness')) {
                previewFilters.brightness = 140;
              }
              if (reason.includes('grayscale')) {
                previewFilters.grayscale = true;
              }
            }

            // Generate preview with suggested filters
            const previewDataUrl = await processImage(imageDataUrl, previewFilters);
            pageImages[pageNumber].preview = previewDataUrl;
          } catch (previewError) {
            console.error(`   ⚠️ Failed to generate preview for page ${pageNumber}:`, previewError);
            // Continue without preview
          }
        }
      }

      // Calculate average savings
      const avgSavings = optimizeCount > 0 ? Math.round(totalSavings / optimizeCount) : 0;

      console.log(`✅ Analysis complete:`, {
        totalPages: analysisResults.length,
        preserveCount,
        optimizeCount,
        avgSavings,
      });

      // Check if we got any results
      if (analysisResults.length === 0) {
        console.log('⚠️ No pages were analyzed (all skipped)');
        alert('No pages could be analyzed. Please ensure pages are loaded.');
        return;
      }

      // Create analysis object
      const analysis = {
        pages: analysisResults,
        summary: {
          avgSavings,
          preserveCount,
          optimizeCount,
        },
        pageImages, // Include page images for preview
      };

      console.log('📊 Analysis object:', analysis);
      console.log('📊 Analysis pages count:', analysis.pages.length);
      console.log('📊 Page images keys:', Object.keys(pageImages));

      // Show dialog or apply directly based on settings
      // Default to true if undefined (safe default - always show review)
      const shouldRequireReview = autoOptimizeSettings.requireReview !== false;
      console.log('🔍 Checking requireReview setting:', autoOptimizeSettings.requireReview, '→ shouldRequireReview:', shouldRequireReview);
      if (shouldRequireReview) {
        console.log('📋 Opening review dialog...');
        console.log('📋 Setting analysis state...');
        setAutoOptimizeAnalysis(analysis);
        console.log('📋 Setting dialog visibility...');
        setShowAutoOptimizeDialog(true);
        console.log('📋 Dialog state should be set now');
      } else {
        console.log('⚡ Auto-applying without review...');
        // Apply automatically without review
        handleApplyAutoOptimize(analysisResults);
      }
    } catch (error) {
      console.error('❌ Error analyzing pages for auto-optimize:', error);
      console.error('Error stack:', error.stack);
      alert(`Failed to analyze pages: ${error.message}\n\nCheck console for details.`);
    } finally {
      // Hide progress overlay
      setIsAnalyzingPages(false);
      setAnalysisProgress({ current: 0, total: 0 });
    }
  };

  const handleApplyAutoOptimize = (pagesToApply) => {
    console.log('🎯 Applying auto-optimize to', pagesToApply.length, 'pages');

    // Apply optimizations to approved pages
    pagesToApply.forEach((page) => {
      if (page.suggestion === 'preserve') {
        // Preserve this page as original
        console.log(`🔒 Preserving page ${page.pageNumber} (marked as already optimized)`);
        preservePagesAsOriginal([page.pageNumber]);
      } else if (page.suggestion === 'optimize') {
        let optimizedFilters;
        let label = 'auto-optimized';

        // Check if a preset was suggested for this page
        if (page.suggestedPreset) {
          // Apply the suggested preset
          optimizedFilters = { ...page.suggestedPreset.settings };
          label = page.suggestedPreset.name;
          console.log(`📋 Applying preset "${page.suggestedPreset.name}" to page ${page.pageNumber}`);
        } else {
          // Apply rule-based optimizations
          const effectiveFilters = getEffectiveFilters(page.pageNumber);
          optimizedFilters = { ...effectiveFilters };

          // Apply suggested optimizations based on reason
          if (page.reason.includes('invert')) {
            optimizedFilters.invert = true;
          }
          if (page.reason.includes('brightness')) {
            optimizedFilters.brightness = 140;
          }
          if (page.reason.includes('grayscale')) {
            optimizedFilters.grayscale = true;
          }
          console.log(`⚙️ Applying rule-based optimization to page ${page.pageNumber}`);
        }

        // Apply to page with autoOptimized flag set to true
        addPageException(page.pageNumber, optimizedFilters, label, true);
      }
    });

    // Close dialog
    setShowAutoOptimizeDialog(false);
    setAutoOptimizeAnalysis(null);

    // Show confirmation
    alert(`Auto-optimization applied to ${pagesToApply.length} page${pagesToApply.length !== 1 ? 's' : ''}!`);
  };

  // Use PDF filters and update function
  const filters = pdfFilters;
  const baseUpdateFilter = updatePDFFilter;

  // Wrapper for updateFilter that also updates page exceptions in Custom/Global modes
  const updateFilter = (filterName, value) => {
    // Always update the global filters
    baseUpdateFilter(filterName, value);

    // If in Custom or Global mode, also update the page exception with the new filter value
    if (pageSettingMode === 'custom' || pageSettingMode === 'global') {
      // Determine which pages to update based on view mode
      const pagesToUpdate = currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0
        ? Array.from(selectedContactSheetPages)
        : [currentPDFPage + 1];

      // Update each page
      pagesToUpdate.forEach(pageNum => {
        const exception = pageExceptions[pageNum];

        if (exception && exception.enabled) {
          // Update the exception with the new filter value
          const updatedSettings = {
            ...exception.settings,
            [filterName]: value
          };
          updatePageException(pageNum, updatedSettings, exception.label);
          console.log(`🔧 Updated ${pageSettingMode} exception for page ${pageNum}:`, filterName, '=', value);
        }
      });
    }
  };

  // Global adjustment handlers
  const handleStoreOriginalFilters = () => {
    setOriginalFiltersBeforeGlobal({ ...filters });
  };

  // Create a stable callback for external components to set manual mode change
  const handleSetManualModeChange = useCallback(() => {
    setManualModeChange(true);
    setPageSettingMode(''); // Clear pageSettingMode in App state
  }, []);

  const handleCancelGlobalAdjustment = (setManualFlag) => {
    // Restore original filters
    if (originalFiltersBeforeGlobal) {
      Object.keys(originalFiltersBeforeGlobal).forEach(key => {
        baseUpdateFilter(key, originalFiltersBeforeGlobal[key]);
      });
    }

    // Clear stored filters
    setOriginalFiltersBeforeGlobal(null);

    // Clear preview state
    setPreviewFilters(null);
    setPreviewPages(new Set());

    // Call the CLEAR callback to reset manual mode flag and clear mode in PDFSidebar
    // This allows auto-detection to run and re-select the appropriate mode
    // PDFSidebar will then notify us via onPageSettingModeChange to update our pageSettingMode
    if (clearManualModeChangeCallbackRef.current) {
      clearManualModeChangeCallbackRef.current();
    }
  };

  // Helper function to check for unsaved changes and show confirmation
  const checkUnsavedChanges = useCallback((targetMode = null) => {
    // If switching to the same mode we're already in, don't warn about unsaved changes
    // (This allows re-entering Custom mode with different selections)
    if (targetMode && targetMode === pageSettingMode) {
      return true; // Allow the action to proceed
    }

    // Check for Preset mode with actual changes (user has previewed/selected a preset)
    // Only warn if previewedPresetId exists (indicates user previewed a preset)
    // OR originalPageExceptions exists (indicates preset selection in progress)
    if (pageSettingMode === 'preset' && (previewedPresetId || originalPageExceptions)) {
      const confirmed = window.confirm(
        'You have unsaved Preset changes. Do you want to discard these changes?'
      );
      if (confirmed) {
        // Clear preview filters and pages
        if (setPreviewFilters && setPreviewPages) {
          setPreviewFilters(null);
          setPreviewPages(new Set());
        }

        // Restore original exceptions if they exist
        if (originalPageExceptions) {
          const selectedPages = currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0
            ? Array.from(selectedContactSheetPages)
            : [currentPDFPage + 1];

          // First, remove current exceptions for selected pages
          selectedPages.forEach(pageNum => {
            if (pageExceptions[pageNum] && deletePageException) { // Changed onDeleteException to deletePageException
              deletePageException(pageNum);
            }
          });

          // Then restore original exceptions
          Object.keys(originalPageExceptions).forEach(pageNum => {
            const original = originalPageExceptions[pageNum];
            if (original && applyPresetToPages) { // Changed onApplyPreset to applyPresetToPages
              applyPresetToPages(original.presetId, [Number(pageNum)]);
            }
          });

          // Clear preset preview state
          setOriginalPageExceptions(null); // originalPageExceptions is now in App.jsx
          setPreviewedPresetId(null); // previewedPresetId is now in App.jsx
        }

        // Close bottom sheet if open (mobile)
        if (bottomSheetOpen) {
          setBottomSheetOpen(false);
        }

        // Clear mode
        setPageSettingMode('');
        setManualModeChange(false);

        return true; // User confirmed, proceed with action
      }
      return false; // User cancelled, don't proceed
    }

    // Check for Custom adjustment mode
    // On mobile: Only warn if bottom sheet is open (actively in adjustment UI)
    // On desktop: Warn if in Custom mode (AdjustmentPanel is visible on right side)
    if (pageSettingMode === 'custom') {
      const isMobile = window.innerWidth < 768;
      const shouldWarn = isMobile ? bottomSheetOpen : true; // Desktop: always warn when in Custom mode

      if (shouldWarn) {
        // Check if filters have actually changed
        let filtersChanged = false;
        if (originalCustomFilters) { // originalCustomFilters is now in App.jsx
          // Compare current filters with original filters
          const filterKeys = Object.keys(filters);
          filtersChanged = filterKeys.some(key => filters[key] !== originalCustomFilters[key]);
        }

        // Only show confirmation if filters have actually changed
        if (filtersChanged) {
          const confirmed = window.confirm(
            'You have unsaved Custom adjustment changes. Do you want to discard these changes?'
          );
          if (confirmed) {
            // Close bottom sheet if open (mobile)
            if (bottomSheetOpen) {
              setBottomSheetOpen(false);
            }

            // Clear preview filters and pages
            if (setPreviewFilters && setPreviewPages) {
              setPreviewFilters(null);
              setPreviewPages(new Set());
            }

            // Clear mode and original filters
            setPageSettingMode('');
            setManualModeChange(false);
            setOriginalCustomFilters(null); // originalCustomFilters is now in App.jsx

            return true; // User confirmed, proceed with action
          }
          return false; // User cancelled, don't proceed
        } else {
          // No changes made, just close without confirmation
          if (bottomSheetOpen) {
            setBottomSheetOpen(false);
          }

          // Clear preview filters and pages even when no changes
          if (setPreviewFilters && setPreviewPages) {
            setPreviewFilters(null);
            setPreviewPages(new Set());
          }

          setPageSettingMode('');
          setManualModeChange(false);
          setOriginalCustomFilters(null); // originalCustomFilters is now in App.jsx
          return true; // No changes, proceed with action
        }
      }
    }

    return true; // No unsaved changes, proceed with action
  }, [previewFilters, pageSettingMode, bottomSheetOpen, currentViewMode, selectedContactSheetPages, currentPDFPage, pageExceptions, deletePageException, applyPresetToPages, filters, setPreviewFilters, setPreviewPages, originalPageExceptions, previewedPresetId, originalCustomFilters]);


  // Get effective filters with preview support
  const getEffectiveFiltersWithPreview = (pageNumber) => {
    // If this page has preview filters, return preview
    if (previewFilters && previewPages.has(pageNumber)) {
      console.log(`[Preview] Page ${pageNumber} using preview filters:`, previewFilters);
      return previewFilters;
    }
    // Otherwise, return normal effective filters
    const normalFilters = getEffectiveFilters(pageNumber);
    console.log(`[Preview] Page ${pageNumber} using normal filters:`, normalFilters);
    return normalFilters;
  };

  // PDF navigation functions
  const goToFirstPage = () => setCurrentPDFPage(0);
  const goToPreviousPage = () => setCurrentPDFPage((prev) => Math.max(0, prev - 1));
  const goToNextPage = () => setCurrentPDFPage((prev) => Math.min(extractedPages.length - 1, prev + 1));
  const goToLastPage = () => setCurrentPDFPage(extractedPages.length - 1);

  return (
    <ThemeProvider>
      <div className="relative h-screen flex flex-col bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 transition-colors overflow-hidden">
        <div className="flex-1 min-h-0 flex flex-col px-2 py-2 sm:px-4 sm:py-3">
          {!uploadedFile && (
            <header className="text-center mb-4 flex-shrink-0">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 dark:text-gray-100 mb-2">
                EcoPrint
              </h1>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">
                Convert dark mode screenshots and PDFs to printer-friendly files
              </p>
            </header>
          )}

        {!uploadedFile ? (
          <ImageUploader onImageUpload={handleFileUpload} />
        ) : (
          /* PDF Layout with Sidebar */
          <>
            <div className="flex-1 min-h-0 flex flex-col md:flex-row">
              {/* Center Pane - Preview - Uses flex-col on mobile, flex-1 on desktop */}
              <div className="flex-1 min-h-0 flex flex-col ml-0 md:ml-2 md:ml-3 lg:ml-4 md:order-2">
                <PDFRightPane
                pdfFile={uploadedFile.file}
                progress={extractProgress}
                isProcessing={isExtracting}
                error={error}
                extractedPages={extractedPages}
                currentProcessedPage={currentProcessedPage}
                currentPageIndex={currentPDFPage}
                isProcessingCurrentPage={isProcessingPage}
                showOriginal={showOriginal}
                onToggleShowOriginal={() => setShowOriginal(!showOriginal)}
                onFirstPage={goToFirstPage}
                onPreviousPage={goToPreviousPage}
                onNextPage={goToNextPage}
                onLastPage={goToLastPage}
                totalPages={extractedPages.length}
                pageExceptions={pageExceptions}
                onPageClick={setCurrentPDFPage}
                onPreservePages={preservePagesAsOriginal}
                processedPagesCache={processedPagesCache}
                filters={pdfFilters}
                getEffectiveFilters={getEffectiveFiltersWithPreview}
                onProcessPages={processMultiplePages}
                savedPresets={savedPresets}
                onApplyPreset={applyPresetToPages}
                initialViewMode={initialViewMode}
                onViewModeChange={handleViewModeChange}
                onSelectedPagesChange={handleSelectedPagesChange}
              />
              </div>

              {/* Sidebar with dynamic width - Footer on mobile (sticky bottom), FIRST on desktop */}
              <div
                style={{ width: isMobile ? '100%' : `${sidebarWidth}px` }}
                className="sticky bottom-0 z-10 flex-shrink-0 md:static md:order-1"
              >
                <PDFSidebar
                  pdfFile={uploadedFile.file}
                  currentPageIndex={currentPDFPage}
                  totalPages={extractedPages.length || 0}
                  onFirstPage={goToFirstPage}
                  onPreviousPage={goToPreviousPage}
                  onNextPage={goToNextPage}
                  onLastPage={goToLastPage}
                  filters={filters}
                  onFilterChange={updateFilter}
                  onReset={handleReset}
                  onDownload={downloadProcessedPDF}
                  isProcessing={isExtracting || isProcessingPage}
                  hasError={!!error}
                  onOpenSettings={() => setShowSettings(true)}
                  pageExceptions={pageExceptions}
                  currentPageNumber={currentPDFPage + 1}
                  hasPageException={!!pageExceptions?.[currentPDFPage + 1]?.enabled}
                  onApplyToCurrentPage={addPageException}
                  onManageExceptions={() => setShowExceptionManager(true)}
                  onSavePreset={handleSavePreset}
                  savedPresets={savedPresets}
                  onApplyPreset={applyPresetToPages}
                  onDeleteException={deletePageException}
                  onPreservePage={preservePagesAsOriginal}
                  currentViewMode={currentViewMode}
                  selectedContactSheetPages={selectedContactSheetPages}
                  onAutoOptimize={handleAutoOptimize}
                  onPageSettingModeChange={setPageSettingMode} // Directly pass setter
                  onForcePresetCreationChange={setForcePresetCreation}
                  previewFilters={previewFilters}
                  setPreviewFilters={setPreviewFilters}
                  setPreviewPages={setPreviewPages}
                  onCancelPreview={() => {
                    setPreviewFilters(null);
                    setPreviewPages(new Set());
                  }}
                  // Lifted states and setters
                  manualModeChange={manualModeChange}
                  setManualModeChange={setManualModeChange}
                  modeWasAutoSelected={modeWasAutoSelected}
                  setModeWasAutoSelected={setModeWasAutoSelected}
                  pageSettingMode={pageSettingMode}
                  setPageSettingMode={setPageSettingMode}
                  bottomSheetOpen={bottomSheetOpen}
                  setBottomSheetOpen={setBottomSheetOpen}
                  originalPageExceptions={originalPageExceptions}
                  setOriginalPageExceptions={setOriginalPageExceptions}
                  previewedPresetId={previewedPresetId}
                  setPreviewedPresetId={setPreviewedPresetId}
                  originalCustomFilters={originalCustomFilters}
                  setOriginalCustomFilters={setOriginalCustomFilters}
                  checkUnsavedChanges={checkUnsavedChanges}
                  handleSetManualModeChange={handleSetManualModeChange}
                />
              </div>

              {/* Resizer Handle - hidden on mobile, visible on desktop, between sidebar and preview */}
              <div
                onMouseDown={() => setIsResizing(true)}
                className="hidden md:block w-1 hover:w-2 bg-gray-300 dark:bg-gray-600 hover:bg-indigo-500 dark:hover:bg-indigo-400 cursor-col-resize transition-all flex-shrink-0 order-none"
                title="Drag to resize sidebar"
              />

              {/* Right Panel - Always visible on desktop, HIDDEN on mobile */}
              <div className="hidden md:flex md:flex-col md:w-80 md:h-full md:min-h-0 flex-shrink-0 md:ml-2 lg:ml-3 xl:ml-4 overflow-hidden order-3">
                {/* Adjustment Controls - Only show when in custom/global/preset mode */}
                {(pageSettingMode === 'custom' ||
                  pageSettingMode === 'global' ||
                  pageSettingMode === 'preset') && (
                                        <AdjustmentPanel
                                          filters={filters}
                                          onFilterChange={updateFilter}
                                          pageSettingMode={pageSettingMode}
                                          currentPageNumber={currentPDFPage + 1}
                                          onSaveAsGlobal={handleSaveAsGlobal}
                                          onApplyGlobalToPages={applyGlobalToPages}
                                          onStoreOriginalFilters={handleStoreOriginalFilters}
                                          onCancelGlobalAdjustment={() => handleCancelGlobalAdjustment(setManualModeChangeCallbackRef.current)}
                                          setBottomSheetOpen={setBottomSheetOpen}
                                          handleSetManualModeChange={handleSetManualModeChange}
                                          checkUnsavedChanges={checkUnsavedChanges}
                                          pageExceptions={pageExceptions}
                                          currentViewMode={currentViewMode}
                                          selectedContactSheetPages={selectedContactSheetPages}
                                          totalPages={extractedPages?.length || 0}
                                          onDeleteException={deletePageException}
                                          setManualModeChange={setManualModeChange}
                                          setPageSettingMode={setPageSettingMode}
                                          setModeWasAutoSelected={setModeWasAutoSelected}
                                          savedPresets={savedPresets}
                                          onApplyPreset={applyPresetToPages}
                                          onSavePreset={handleSavePreset}
                                          onApplyToCurrentPage={addPageException}
                                          onCancelCustomAdjustment={() => {
                                            // Call the callback to restore original filters and clear custom mode
                                            if (cancelCustomAdjustmentCallbackRef.current) {
                                              cancelCustomAdjustmentCallbackRef.current();
                                            }
                                          }}
                                          onClosePanel={(forceClose = false, appliedCustom = false) => {
                                            console.log('[onClosePanel] Called with forceClose:', forceClose, 'appliedCustom:', appliedCustom);
                                            console.log('[onClosePanel] Current pageSettingMode:', pageSettingMode);
                    
                                            // If custom was just applied, close panel but restore mode after to keep button highlighted
                                            if (appliedCustom) {
                                              console.log('[onClosePanel] Custom was applied, closing panel but will restore mode');
                                              if (clearManualModeChangeCallbackRef.current) {
                                                clearManualModeChangeCallbackRef.current(); // Close the panel
                                              }
                                              // Restore mode immediately to keep button highlighted (using setTimeout to ensure state update happens after clear)
                                              setTimeout(() => {
                                                console.log('[onClosePanel] Restoring custom mode to keep button highlighted');
                                                if (setManualModeChangeCallbackRef.current) {
                                                  setManualModeChangeCallbackRef.current(true); // Set manual mode change flag
                                                }
                                                // Use PDFSidebar's setPageSettingMode to update its internal state
                                                if (setPageSettingModeCallbackRef.current) {
                                                  setPageSettingModeCallbackRef.current('custom'); // Set PDFSidebar's pageSettingMode
                                                  console.log('[onClosePanel] Called PDFSidebar setPageSettingMode to restore custom mode');
                                                }
                                              }, 0);
                                              return;
                                            }
                    
                                            // Otherwise, check if any of the currently selected pages have custom adjustments
                                            const selectedPages = currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0
                                              ? Array.from(selectedContactSheetPages)
                                              : [currentPDFPage + 1];
                    
                                            const hasCustomAdjustments = selectedPages.some(pageNum => {
                                              const exception = pageExceptions[pageNum];
                                              return exception && exception.enabled && exception.label === '';
                                            });
                    
                                            console.log('[onClosePanel] hasCustomAdjustments:', hasCustomAdjustments);
                                            console.log('[onClosePanel] forceClose:', forceClose);
                    
                                            // Only clear mode if pages don't have custom adjustments
                                            if (!hasCustomAdjustments && clearManualModeChangeCallbackRef.current) {
                                              console.log('[onClosePanel] No custom adjustments, clearing mode');
                                              clearManualModeChangeCallbackRef.current();
                                            } else {
                                              console.log('[onClosePanel] Has custom adjustments, keeping mode and allowing button to stay highlighted');
                                              // If pages have custom adjustments, mode stays 'custom' to keep button highlighted
                                              // But we need to allow the button to be clickable again to reopen panel
                                              // So we keep manualModeChange but clear the bottom sheet if it was forceClose
                                            }
                                          }}
                                                                                    originalPageExceptions={originalPageExceptions}
                                          setOriginalPageExceptions={setOriginalPageExceptions}
                                          previewedPresetId={previewedPresetId}
                                          setPreviewedPresetId={setPreviewedPresetId}
                                          originalCustomFilters={originalCustomFilters}
                                          setOriginalCustomFilters={setOriginalCustomFilters}
                                        />                )}

                {/* Advertising Area - Always visible */}
                <AdSenseBlock />
              </div>
            </div>

            <SettingsDialog
              isOpen={showSettings}
              onClose={() => setShowSettings(false)}
              settings={settings}
              onSettingsChange={updateSettings}
              onProcessAll={processAllPages}
              savedPresets={savedPresets}
            />

            <AutoOptimizeDialog
              isOpen={showAutoOptimizeDialog}
              onClose={() => {
                setShowAutoOptimizeDialog(false);
                setAutoOptimizeAnalysis(null);
              }}
              onApply={handleApplyAutoOptimize}
              analysis={autoOptimizeAnalysis}
              pageImages={autoOptimizeAnalysis?.pageImages}
            />

            {/* Analysis Progress Overlay */}
            {isAnalyzingPages && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
                  <div className="text-center">
                    <div className="mb-4">
                      <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-2">
                      Analyzing Pages for Ink Savings
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                      Calculating ink usage and optimization strategies...
                    </p>
                    <div className="mb-2">
                      <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
                        <span>Progress</span>
                        <span>{analysisProgress.current} / {analysisProgress.total} pages</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-indigo-600 dark:bg-indigo-400 h-2 rounded-full transition-all duration-300"
                          style={{
                            width: `${analysisProgress.total > 0 ? (analysisProgress.current / analysisProgress.total) * 100 : 0}%`
                          }}
                        ></div>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
                      This may take a moment for large documents...
                    </p>
                  </div>
                </div>
              </div>
            )}

            {showExceptionManager && (
              <PageExceptionManager
                pageExceptions={pageExceptions}
                onAddException={addPageException}
                onUpdateException={updatePageException}
                onDeleteException={deletePageException}
                onToggleException={togglePageException}
                totalPages={extractedPages.length}
                currentPageIndex={currentPDFPage}
                onClose={() => setShowExceptionManager(false)}
              />
            )}
          </>
        )}
        </div>
      </div>
    </ThemeProvider>
  );
}

export default App;
